import { useCallback, useRef, useState } from 'react';
import { getFileStore, jobPath } from '@ppdf/pdf-engine/opfs';
import { HANDOFF_KEY, type HandoffEntry } from './DropZone.js';
import { Upload } from '../ui/icons.js';

/**
 * Images to PDF — plan §10, core catalogue.
 *
 * THE CONVERSION HAPPENS AT THE DOOR, and that is the whole design. Rather
 * than a tool inside the editor that understands images, the dropped pictures
 * are turned into a PDF here and handed to the editor as an ordinary document.
 * Everything downstream — reorder, crop, compress, protect, page numbers —
 * then works on them with no special cases anywhere.
 *
 * pdf-lib is loaded by DYNAMIC IMPORT, only once someone actually drops a
 * file. It is ~300 KB, and this page is budgeted at 30 KB of JavaScript
 * (plan §12.4); a static import would put the whole PDF library on the
 * critical path of a page whose job is to show a drop zone.
 */

const IMAGES_JOB = 'images-to-pdf';

/** Page sizes people actually ask for, in PDF points. */
const PAGE_SIZES = {
  fit: null,
  a4: { width: 595.28, height: 841.89 },
  letter: { width: 612, height: 792 },
} as const;

type SizeKey = keyof typeof PAGE_SIZES;

export function ImagesToPdf() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [size, setSize] = useState<SizeKey>('fit');
  const [margin, setMargin] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const convert = useCallback(
    async (files: FileList | null) => {
      const images = files
        ? Array.from(files).filter((f) => /^image\/(png|jpeg)$/i.test(f.type))
        : [];

      if (images.length === 0) {
        setError('Choose PNG or JPEG images — those are what a PDF can hold.');
        return;
      }

      setError(null);
      setBusy('Loading the converter…');

      try {
        // Only now, and only once. See the note at the top of this file.
        const { PDFDocument } = await import('pdf-lib');

        setBusy('Building the document…');
        const doc = await PDFDocument.create();

        // Sorted by name so "IMG_001, IMG_002, IMG_010" lands in that order
        // rather than in whatever order the file picker handed them over.
        images.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

        for (const [index, file] of images.entries()) {
          setBusy(`Adding image ${index + 1} of ${images.length}…`);
          const bytes = new Uint8Array(await file.arrayBuffer());
          const embedded = /png/i.test(file.type)
            ? await doc.embedPng(bytes)
            : await doc.embedJpg(bytes);

          const fixed = PAGE_SIZES[size];
          if (!fixed) {
            // One page per image, exactly its own size: no letterboxing, no
            // upscaling, nothing added that was not in the picture.
            const page = doc.addPage([embedded.width, embedded.height]);
            page.drawImage(embedded, {
              x: 0,
              y: 0,
              width: embedded.width,
              height: embedded.height,
            });
            continue;
          }

          const page = doc.addPage([fixed.width, fixed.height]);
          const inset = (margin / 100) * Math.min(fixed.width, fixed.height);
          const box = {
            width: fixed.width - inset * 2,
            height: fixed.height - inset * 2,
          };
          // Contain, never crop: an image that does not match the page shape
          // gets whitespace rather than losing its edges.
          const scale = Math.min(box.width / embedded.width, box.height / embedded.height);
          const drawn = { width: embedded.width * scale, height: embedded.height * scale };
          page.drawImage(embedded, {
            x: (fixed.width - drawn.width) / 2,
            y: (fixed.height - drawn.height) / 2,
            width: drawn.width,
            height: drawn.height,
          });
        }

        setBusy('Opening the editor…');
        const pdf = await doc.save();

        // Staged in OPFS and handed over exactly like a dropped PDF, so the
        // editor needs no idea this document began life as photographs.
        const path = jobPath(IMAGES_JOB, 'from-images.pdf');
        await getFileStore().writeStream(
          path,
          new Blob([pdf as BlobPart], { type: 'application/pdf' }).stream(),
        );

        const staged: HandoffEntry[] = [
          { path, name: 'from-images.pdf', size: pdf.byteLength, at: Date.now() },
        ];
        sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(staged));
        window.location.href = '/app?tool=organize';
      } catch (err) {
        setBusy(null);
        setError(
          err instanceof Error ? `Could not convert: ${err.message}` : 'Could not convert.',
        );
      }
    },
    [size, margin],
  );

  return (
    <div className="mt-8">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void convert(e.dataTransfer.files);
        }}
        className={`relative overflow-hidden rounded-2xl border p-8 text-center transition-all duration-200 sm:p-10 ${
          dragging
            ? 'border-brand bg-brand/[0.07] shadow-lift'
            : 'border-line bg-surface shadow-sm hover:border-line-strong'
        }`}
      >
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-surface-2 text-muted">
          <Upload className="h-5 w-5" />
        </div>

        <button
          type="button"
          disabled={busy !== null}
          onClick={() => inputRef.current?.click()}
          className="btn btn-primary mt-5 px-6 py-3 text-[0.9375rem]"
        >
          {busy ?? 'Choose images'}
        </button>

        <p className="mt-3 text-sm text-muted">or drop PNG and JPEG files here</p>

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg"
          multiple
          className="hidden"
          onChange={(e) => void convert(e.target.files)}
        />

        <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-muted">Page size</span>
            <select
              value={size}
              onChange={(e) => setSize(e.target.value as SizeKey)}
              className="field w-auto"
            >
              <option value="fit">Fit each image</option>
              <option value="a4">A4</option>
              <option value="letter">Letter</option>
            </select>
          </label>

          {size !== 'fit' && (
            <label className="flex items-center gap-2">
              <span className="text-muted">Margin</span>
              <select
                value={margin}
                onChange={(e) => setMargin(Number(e.target.value))}
                className="field w-auto"
              >
                <option value={0}>None</option>
                <option value={5}>Small</option>
                <option value={10}>Large</option>
              </select>
            </label>
          )}
        </div>

        <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3 py-1.5 font-mono-ui text-[0.6875rem] text-faint">
          <span aria-hidden className="dot dot-ok" />
          converted locally · 0 bytes sent
        </p>
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
